import React from "react";
import type { Theme } from "../lib/theme";
import type { BuyoutItem, ShowToast } from "../types";
import type { BidScope, BidPartner, Bid } from "../projectData";
import * as Icon from "../components/OEIcons";
import { statusAfterContract } from "../lib/buyoutStatus";
import { buyoutCoverage, coverageNote } from "../lib/buyoutCoverage";
import { walkItems } from "../lib/calculations";

const FONT = "'Inter','Avenir Next','Century Gothic',sans-serif";

interface BuyoutTabProps {
  T: Theme;
  buyoutItems: BuyoutItem[];
  setBuyoutItems: React.Dispatch<React.SetStateAction<BuyoutItem[]>>;
  editingBuyoutCell: { id: string; field: "budget" | "contract" } | null;
  setEditingBuyoutCell: React.Dispatch<
    React.SetStateAction<{ id: string; field: "budget" | "contract" } | null>
  >;
  divisions: any[];
  bids: Bid[];
  bidScopes: BidScope[];
  bidPartners: BidPartner[];
  itemState: Record<string, any>;
  snapshot: () => void;
  uid: () => string;
  showToast: ShowToast;
  fmt: (n: number) => string;
  setShowSowEditor: (v: { divisionId: string; divisionName: string; tradeName: string; tradeContact: string } | null) => void;
}

export default function BuyoutTab({
  T, buyoutItems, setBuyoutItems, editingBuyoutCell, setEditingBuyoutCell,
  divisions, bids, bidScopes, bidPartners, itemState,
  snapshot, uid, showToast, fmt, setShowSowEditor,
}: BuyoutTabProps) {
  const addBuyout = () => {
    snapshot();
    setBuyoutItems((prev) => [
      ...prev,
      {
        id: uid(), scope: "", divisionId: "", budgetAmount: 0, vendor: "", bidId: "",
        contractAmount: 0, contractNumber: "", contractDate: "", status: "pending", notes: "",
      },
    ]);
  };

  const updateBuyout = (id: string, patch: Partial<BuyoutItem>) =>
    setBuyoutItems((prev) => prev.map((b) => (b.id === id ? { ...b, ...patch } : b)));

  const deleteBuyout = (id: string) => {
    snapshot();
    setBuyoutItems((prev) => prev.filter((b) => b.id !== id));
  };

  const autoPopulate = () => {
    snapshot();
    const existingSubIds = new Set(buyoutItems.map((b) => b.subId).filter(Boolean));
    const existingNames = new Set(buyoutItems.map((b) => b.scope.toLowerCase().trim()));

    const divVendorMap = new Map<string, string>();
    bidScopes.forEach((scope) => {
      const active = bids.filter((b) => b.scopeId === scope.id && b.status === "Active");
      if (!active.length) return;
      const best = active.sort((a, b2) => b2.version - a.version)[0];
      const partner = bidPartners.find((p) => p.id === best.partnerId) || null;
      if (partner?.name) scope.divisionIds.forEach((divId: string) => divVendorMap.set(divId, partner.name));
    });

    const SKIP_DIVS = new Set(["div1"]);
    const newItems: BuyoutItem[] = [];

    divisions.forEach((div) => {
      if (SKIP_DIVS.has(div.id)) return;
      (div.subcategories || []).forEach((sub: any) => {
        if (existingSubIds.has(sub.id)) return;
        if (existingNames.has((sub.name || "").toLowerCase().trim())) return;

        const included = [...walkItems(sub.rows || [])].map((item) => ({
          item,
          li: itemState[item.id],
        })).filter(({ li }) => li && li.included);
        if (!included.length) return;

        const budgetAmt = included.reduce((s, { li }) => s + (li.qty || 0) * (li.unitCost || 0), 0);
        if (budgetAmt === 0) return;

        newItems.push({
          id: uid(), scope: sub.name, divisionId: div.id, subId: sub.id,
          budgetAmount: Math.round(budgetAmt), vendor: divVendorMap.get(div.id) || "",
          bidId: "", contractAmount: 0, contractNumber: "", contractDate: "",
          status: "pending", notes: "",
        });
      });
    });

    if (newItems.length > 0) {
      setBuyoutItems((prev) => [...prev, ...newItems]);
      showToast(`Added ${newItems.length} scope${newItems.length !== 1 ? "s" : ""} from estimate`);
    } else {
      showToast("All estimate subcategories are already in the log.");
    }
  };

  const totalBudget = buyoutItems.reduce((s, b) => s + b.budgetAmount, 0);
  const totalContract = buyoutItems.filter((b) => b.contractAmount > 0).reduce((s, b) => s + b.contractAmount, 0);
  const boughtOutCount = buyoutItems.filter((b) => ["awarded", "executed"].includes(b.status)).length;
  const comparable = buyoutItems.filter((b) => b.budgetAmount > 0 && b.contractAmount > 0);
  const compBudget = comparable.reduce((s, b) => s + b.budgetAmount, 0);
  const compContract = comparable.reduce((s, b) => s + b.contractAmount, 0);
  const compVariance = compContract - compBudget;
  const compVariancePct = compBudget > 0 ? (compVariance / compBudget) * 100 : 0;
  const overBudgetCount = comparable.filter((b) => b.contractAmount > b.budgetAmount).length;
  const underBudgetCount = comparable.filter((b) => b.contractAmount < b.budgetAmount).length;

  // What this log is currently contributing to Estimate Accuracy, and how
  // close the rest is. "Eleven numbers and this job joins your history" is a
  // far better prompt than a tab that explains nothing about why to fill it.
  const coverage = buyoutCoverage(buyoutItems);

  const STATUS_OPTS = ["pending", "bidding", "awarded", "executed"];
  const STATUS_COLOR: Record<string, string> = {
    pending: T.textMuted, bidding: T.borderAccent, awarded: T.positive, executed: "#a78bfa",
  };
  const STATUS_LABEL: Record<string, string> = {
    pending: "Pending", bidding: "Bidding", awarded: "Awarded", executed: "Executed",
  };

  return (
    <div className="fade-in" style={{ display: "flex", flexDirection: "column", height: "100%", overflow: "hidden", fontFamily: FONT }}>
      {/* Toolbar */}
      <div style={{ padding: "14px 20px 10px", borderBottom: `1px solid ${T.borderPrimary}`, flexShrink: 0, display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        <div style={{ fontSize: 16, fontWeight: 700, color: T.borderAccent, flex: 1 }}>Buyout Log</div>
        <button onClick={autoPopulate}
          style={{ background: "none", border: `1px solid ${T.borderSecondary}`, color: T.textTertiary, cursor: "pointer", padding: "6px 14px", borderRadius: 6, fontSize: 11, fontFamily: FONT, display: "flex", alignItems: "center", gap: 6 }}>
          <Icon.Download size={11} /> Sync from Estimate
        </button>
        <button onClick={addBuyout}
          style={{ background: T.borderAccent, border: "none", color: T.textOnGold, cursor: "pointer", padding: "7px 16px", borderRadius: 6, fontSize: 12, fontFamily: FONT, fontWeight: 700, display: "flex", alignItems: "center", gap: 6 }}>
          <Icon.Plus size={11} /> Add Scope
        </button>
      </div>

      {/* The denominator, always, not a nag when something is missing.
          This log is filled in by hand from numbers that live in an ERP, so
          it is never complete, and the rows that do get entered are the ones
          that surprised somebody. Saying how much is recorded is what lets a
          reader weigh the variance instead of believing it. The old banner
          here promised the opposite -- that filling it in would reveal "which
          trades you run light on" -- which is a verdict this data cannot
          support and an invitation to trust it more the less of it there is. */}
      {coverage.total > 0 && (
        <div style={{ padding: "9px 20px", borderBottom: `1px solid ${T.borderPrimary}`,
          background: `${T.borderAccent}0d`, fontSize: 11, color: T.textSecondary, lineHeight: 1.55, flexShrink: 0 }}>
          <strong style={{ color: T.textPrimary }}>{coverageNote(coverage)}</strong>
          {coverage.valuePct < 0.999 && (
            <> — figures below cover only what is recorded. A partial log reads
            high-variance by construction, because the scope you remember to enter
            is usually the one that surprised you.</>
          )}
        </div>
      )}

      {/* Summary bar */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 0, borderBottom: `1px solid ${T.borderPrimary}`, flexShrink: 0 }}>
        {[
          { label: "Total Budget", value: fmt(totalBudget), color: T.textSecondary, sub: `Across ${buyoutItems.length} scope${buyoutItems.length !== 1 ? "s" : ""}` },
          { label: "Total Contract", value: totalContract > 0 ? fmt(totalContract) : "—", color: T.textSecondary, sub: `${comparable.length} of ${buyoutItems.length} with contract` },
          {
            label: "Variance vs Budget",
            value: comparable.length > 0 ? (compVariance === 0 ? "On budget" : `${compVariance > 0 ? "+" : ""}${fmt(compVariance)}`) : "—",
            color: compVariance > 0 ? T.negative : compVariance < 0 ? T.positive : T.textMuted,
            sub: comparable.length > 0 ? `${compVariancePct > 0 ? "+" : ""}${compVariancePct.toFixed(1)}% on contracted scope` : "Awaiting contracts",
          },
          {
            label: "Scopes Bought Out", value: `${boughtOutCount} / ${buyoutItems.length}`, color: T.borderAccent,
            sub: comparable.length > 0 ? `${overBudgetCount} over · ${underBudgetCount} under` : "—",
          },
        ].map(({ label, value, color, sub }) => (
          <div key={label} style={{ padding: "12px 20px", borderRight: `1px solid ${T.borderPrimary}` }}>
            <div style={{ fontSize: 10, color: T.textMuted, letterSpacing: ".08em", textTransform: "uppercase", marginBottom: 4 }}>{label}</div>
            <div style={{ fontSize: 20, fontWeight: 700, color }}>{value}</div>
            {sub && <div style={{ fontSize: 10, color: T.textWeak, marginTop: 3 }}>{sub}</div>}
          </div>
        ))}
      </div>

      {/* Table */}
      <div style={{ flex: 1, overflowY: "auto" }}>
        {buyoutItems.length === 0 ? (
          <div style={{ padding: "48px 24px", textAlign: "center", color: T.textMuted }}>
            <div style={{ marginBottom: 12, opacity: .25 }}><Icon.Folder size={32} /></div>
            <div style={{ fontSize: 13, fontWeight: 600, color: T.textWeak, marginBottom: 6 }}>No buyout items yet</div>
            <div style={{ fontSize: 11 }}>Add scopes manually or click <strong style={{ color: T.textWeak }}>Sync from Estimate</strong> to pull in your cost breakdown by subcategory.</div>
          </div>
        ) : (
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
            <thead>
              <tr style={{ background: T.bgPanel, borderBottom: `1px solid ${T.borderPrimary}` }}>
                {["Scope / Trade", "Division", "Budget", "Vendor", "Contract Amt", "Variance", "Status", "Contract #", "Date", "Notes", "SOW", ""].map((h) => (
                  <th key={h} style={{ padding: "7px 12px", textAlign: "left", fontSize: 10, fontWeight: 700, color: T.textMuted, letterSpacing: ".08em", textTransform: "uppercase", whiteSpace: "nowrap" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {buyoutItems.map((b) => {
                const variance = b.contractAmount > 0 ? b.contractAmount - b.budgetAmount : null;
                return (
                  <tr key={b.id} style={{ borderBottom: `1px solid ${T.borderPrimary}22` }}>
                    <td style={{ padding: "7px 10px", minWidth: 160 }}>
                      <input value={b.scope} onChange={(e) => updateBuyout(b.id, { scope: e.target.value })} placeholder="Scope name…"
                        style={{ background: "none", border: "none", outline: "none", color: T.textSecondary, fontSize: 11, fontFamily: FONT, fontWeight: 600, width: "100%" }} />
                    </td>
                    <td style={{ padding: "7px 10px", minWidth: 120 }}>
                      <select value={b.divisionId} onChange={(e) => updateBuyout(b.id, { divisionId: e.target.value })}
                        style={{ background: T.bgApp, border: `1px solid ${T.borderSecondary}`, color: T.textTertiary, padding: "3px 6px", borderRadius: 3, fontSize: 10, fontFamily: FONT, outline: "none", maxWidth: 130 }}>
                        <option value="">—</option>
                        {divisions.map((d) => (
                          <option key={d.id} value={d.id}>{(d.name || d.id).replace(/^Division \d+ [–-] /, "").replace(/^Div \d+ [–-] /, "").substring(0, 22)}</option>
                        ))}
                      </select>
                    </td>
                    <td style={{ padding: "7px 10px", textAlign: "right", minWidth: 100 }}>
                      {editingBuyoutCell?.id === b.id && editingBuyoutCell?.field === "budget" ? (
                        <input autoFocus type="number" defaultValue={b.budgetAmount || 0}
                          onBlur={(e) => { updateBuyout(b.id, { budgetAmount: parseFloat(e.target.value) || 0 }); setEditingBuyoutCell(null); }}
                          onKeyDown={(e) => { if (e.key === "Enter" || e.key === "Escape") (e.target as HTMLInputElement).blur(); }}
                          style={{ background: "none", border: "none", outline: "none", color: T.textTertiary, fontSize: 11, fontFamily: FONT, textAlign: "right", width: 90 }} />
                      ) : (
                        <span onClick={() => setEditingBuyoutCell({ id: b.id, field: "budget" })}
                          style={{ color: b.budgetAmount ? T.textTertiary : T.textMuted, cursor: "text", fontVariantNumeric: "tabular-nums", display: "block", textAlign: "right" }}>
                          {b.budgetAmount ? fmt(b.budgetAmount) : "—"}
                        </span>
                      )}
                    </td>
                    <td style={{ padding: "7px 10px", minWidth: 130 }}>
                      <input value={b.vendor} onChange={(e) => updateBuyout(b.id, { vendor: e.target.value })} placeholder="Vendor name…"
                        style={{ background: "none", border: "none", outline: "none", color: T.textSecondary, fontSize: 11, fontFamily: FONT, width: "100%" }} />
                    </td>
                    <td style={{ padding: "7px 10px", textAlign: "right", minWidth: 100 }}>
                      {editingBuyoutCell?.id === b.id && editingBuyoutCell?.field === "contract" ? (
                        <input autoFocus type="number" defaultValue={b.contractAmount || 0}
                          onBlur={(e) => {
                            const amount = parseFloat(e.target.value) || 0;
                            // Entering an amount says the scope was bought out,
                            // so the row stops waiting on a dropdown to agree.
                            updateBuyout(b.id, { contractAmount: amount, status: statusAfterContract(b.status, amount) });
                            setEditingBuyoutCell(null);
                          }}
                          onKeyDown={(e) => { if (e.key === "Enter" || e.key === "Escape") (e.target as HTMLInputElement).blur(); }}
                          style={{ background: "none", border: "none", outline: "none", color: T.textSecondary, fontSize: 11, fontFamily: FONT, textAlign: "right", width: 90 }} />
                      ) : (
                        <span onClick={() => setEditingBuyoutCell({ id: b.id, field: "contract" })}
                          style={{ color: b.contractAmount ? T.textSecondary : T.textMuted, cursor: "text", fontVariantNumeric: "tabular-nums", display: "block", textAlign: "right" }}>
                          {b.contractAmount ? fmt(b.contractAmount) : "—"}
                        </span>
                      )}
                    </td>
                    <td style={{
                      padding: "7px 10px", textAlign: "right", fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap",
                      color: variance === null ? T.textMuted : variance > 0 ? T.negative : variance < 0 ? T.positive : T.textMuted,
                    }}>
                      {variance === null ? "—" : fmt(variance)}
                    </td>
                    <td style={{ padding: "7px 10px", minWidth: 110 }}>
                      <select value={b.status} onChange={(e) => updateBuyout(b.id, { status: e.target.value as BuyoutItem["status"] })}
                        style={{
                          background: `${STATUS_COLOR[b.status] || T.textMuted}18`,
                          border: `1px solid ${STATUS_COLOR[b.status] || T.textMuted}44`,
                          color: STATUS_COLOR[b.status] || T.textMuted,
                          padding: "3px 8px", borderRadius: 10, fontSize: 10, fontFamily: FONT, outline: "none", fontWeight: 700, cursor: "pointer",
                        }}>
                        {STATUS_OPTS.map((s) => <option key={s} value={s}>{STATUS_LABEL[s] || s}</option>)}
                      </select>
                    </td>
                    <td style={{ padding: "7px 10px", minWidth: 100 }}>
                      <input value={b.contractNumber} onChange={(e) => updateBuyout(b.id, { contractNumber: e.target.value })} placeholder="—"
                        style={{ background: "none", border: "none", outline: "none", color: T.textTertiary, fontSize: 11, fontFamily: FONT, width: 80 }} />
                    </td>
                    <td style={{ padding: "7px 10px", minWidth: 100 }}>
                      <input type="date" value={b.contractDate || ""} onChange={(e) => updateBuyout(b.id, { contractDate: e.target.value })}
                        style={{ background: "none", border: "none", outline: "none", color: T.textTertiary, fontSize: 11, fontFamily: FONT }} />
                    </td>
                    <td style={{ padding: "7px 10px", minWidth: 140 }}>
                      <input value={b.notes} onChange={(e) => updateBuyout(b.id, { notes: e.target.value })} placeholder="Notes…"
                        style={{ background: "none", border: "none", outline: "none", color: T.textMuted, fontSize: 11, fontFamily: FONT, width: "100%" }} />
                    </td>
                    <td style={{ padding: "7px 8px", textAlign: "center" }}>
                      {(() => {
                        const div = divisions.find((d) => d.id === b.divisionId);
                        return (
                          <button
                            onClick={() => setShowSowEditor({ divisionId: b.divisionId || "div1", divisionName: div?.name || b.scope || "Trade", tradeName: b.vendor || b.scope || "Trade Partner", tradeContact: "" })}
                            title="Generate Scope of Work for this trade contract"
                            style={{ background: "none", border: `1px solid ${T.borderAccent}55`, borderRadius: 3, color: T.borderAccent, cursor: "pointer", padding: "3px 8px", fontSize: 10, fontWeight: 600, fontFamily: FONT, whiteSpace: "nowrap" }}>
                            SOW
                          </button>
                        );
                      })()}
                    </td>
                    <td style={{ padding: "7px 8px", textAlign: "center" }}>
                      <button onClick={() => deleteBuyout(b.id)}
                        style={{ background: "none", border: "none", color: T.textMuted, cursor: "pointer", padding: "2px 4px", opacity: .5, display: "flex", alignItems: "center" }}
                        onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.color = T.negative; (e.currentTarget as HTMLElement).style.opacity = "1"; }}
                        onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.color = T.textMuted; (e.currentTarget as HTMLElement).style.opacity = ".5"; }}>
                        <Icon.X size={11} />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
